import { apiClient, handleApiRequest } from './client'
import type { LoginRequest, LoginResponse, VerifyRequest, AuthResponse, User, UserRolesResponse } from '@/types'
import type { SessionListResponse, DeviceSessionsResponse, SessionSecurityReport, RevokeSessionResponse } from '@/types/sessions'
import type { Role } from '@/types/role'

export interface VerifyPrepTokenResponse {
  valid: boolean
  expired?: boolean
  error?: string | null
  access_token?: string
  refresh_token?: string
  token_type?: string
  expires_in?: number
  user?: User
  prep_request_id?: string
  request_id?: string
  redirect_url?: string
  teacher_email?: string
  prep_already_fulfilled?: boolean
}

export interface VerifySubstitutionTokenResponse {
  valid: boolean
  expired?: boolean
  error?: string | null
  access_token?: string
  refresh_token?: string
  token_type?: string
  expires_in?: number
  user?: User
  request_id?: string
  redirect_url?: string
  teacher_email?: string
}

export const authApi = {
  requestLogin: (data: LoginRequest) =>
    handleApiRequest<LoginResponse>(apiClient.post('/api/v1/auth/request-login', data)),

  verify: (data: VerifyRequest) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/auth/verify', data)),

  verifyCode: (data: { email: string; code: string }) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/auth/verify-code', data)),

  webauthnRegisterOptions: () =>
    handleApiRequest<Record<string, unknown>>(apiClient.post('/api/v1/auth/webauthn/register/options')),

  webauthnRegister: (credential: Record<string, unknown>, origin: string) =>
    handleApiRequest<{ ok: boolean }>(apiClient.post('/api/v1/auth/webauthn/register', { credential, origin })),

  webauthnAuthenticateOptions: () =>
    handleApiRequest<Record<string, unknown>>(apiClient.post('/api/v1/auth/webauthn/authenticate/options')),

  webauthnAuthenticate: (credential: Record<string, unknown>, origin: string) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/auth/webauthn/authenticate', { credential, origin })),

  verifyPrepToken: (token: string) =>
    handleApiRequest<VerifyPrepTokenResponse>(
      apiClient.post('/api/v1/auth/verify-prep-token', { token })
    ),

  verifySubstitutionToken: (token: string) =>
    handleApiRequest<VerifySubstitutionTokenResponse>(
      apiClient.post('/api/v1/auth/verify-substitution-token', { token })
    ),

  verifyAdminToken: (token: string) =>
    handleApiRequest<VerifySubstitutionTokenResponse>(
      apiClient.post('/api/v1/auth/verify-admin-token', { token })
    ),

  logout: () =>
    handleApiRequest(apiClient.post('/api/v1/auth/logout')),

  logoutAll: () =>
    handleApiRequest(apiClient.post('/api/v1/auth/logout-all')),

  getCurrentUser: () =>
    handleApiRequest<User>(apiClient.get('/api/v1/auth/me')),

  switchRole: (role: Role) =>
    handleApiRequest<User>(apiClient.post('/api/v1/auth/switch-role', { role })),

  getMyRoles: () =>
    handleApiRequest<UserRolesResponse>(apiClient.get('/api/v1/auth/my-roles')),

  selectEngineerSchool: (schoolId: string) =>
    handleApiRequest<AuthResponse>(apiClient.post('/api/v1/auth/engineer/select-school', { school_id: schoolId })),
}

export const sessionApi = {
  getMySessions: () =>
    handleApiRequest<SessionListResponse>(apiClient.get('/api/v1/sessions/my-sessions')),

  getMyDevices: () =>
    handleApiRequest<DeviceSessionsResponse>(apiClient.get('/api/v1/sessions/my-sessions/devices')),

  getSecurityReport: () =>
    handleApiRequest<SessionSecurityReport>(apiClient.get('/api/v1/sessions/my-sessions/security-report')),

  revokeSession: (sessionId: string) =>
    handleApiRequest<RevokeSessionResponse>(apiClient.delete(`/api/v1/sessions/my-sessions/${sessionId}`)),

  revokeAllOtherSessions: () =>
    handleApiRequest<RevokeSessionResponse>(apiClient.delete('/api/v1/sessions/my-sessions')),
}
